// ts-rest contract for the Suitability Inconsistency Flags (SEP-293 section 8).
//
// Endpoints:
//   GET  /api/desk/suitability/:clientId/flags       — the flags of the newest review of a client
//   POST /api/desk/suitability/flags/:flagId/rationale — the IM records a rationale
//   POST /api/comply/suitability/flags/:flagId/note  — Compliance records a note
//   GET  /api/comply/suitability/flags               — the firm-wide register of the flags that Compliance sees
//
// The read reconciles the register first, so the panel shows the flags of the
// answers as they stand.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';
import { suitabilityLifecycleSchema } from '../suitability-assessment-lifecycle';
import type { InternalReviewFactsSnapshot } from '../suitability-flags/facts';

const c = initContract();

const flagValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.string()),
]);

export const suitabilityFlagSeveritySchema = z.enum(['red', 'amber', 'info']);
export const suitabilityFlagStatusSchema = z.enum([
    'open',
    'rationale_recorded',
    'cleared',
    'superseded',
    'withdrawn',
]);

/** One flag of the register, as the Flags panel shows it. */
export const suitabilityFlagDtoSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    clientId: z.string(),
    ruleId: z.string(),
    family: z.string(),
    severity: suitabilityFlagSeveritySchema,
    status: suitabilityFlagStatusSchema,
    trigger: z.string(),
    challenge: z.string(),
    values: z.record(z.string(), flagValueSchema),
    /** The fact-find and ISA fields that the rule reads. */
    sources: z.array(z.string()),
    thresholdsAtRaise: z.record(z.string(), z.number()),
    /** The thresholds of the raise that the firm has not agreed yet. */
    unconfirmedThresholds: z.array(z.string()),
    raisedAt: z.string(),
    occurrence: z.number().int(),
    targetFlagId: z.string().nullable(),
    requiresEnhancedRationale: z.boolean(),
    rationale: z.string().nullable(),
    rationaleByName: z.string().nullable(),
    rationaleAt: z.string().nullable(),
    peerReviewerName: z.string().nullable(),
    peerReviewedAt: z.string().nullable(),
    peerReviewOutcome: z.enum(['agreed', 'challenged']).nullable(),
    peerReviewComment: z.string().nullable(),
    complianceVisible: z.boolean(),
    complianceNote: z.string().nullable(),
    complianceNoteAt: z.string().nullable(),
});
export type SuitabilityFlagDto = z.infer<typeof suitabilityFlagDtoSchema>;

export const suitabilityFlagBlockerSchema = z.object({
    code: z.string(),
    message: z.string(),
    flagId: z.string().optional(),
});

export const suitabilityFlagsResponseSchema = z.object({
    /** The newest review of the client, or null when the client has none. */
    assessmentId: z.string().nullable(),
    lifecycle: suitabilityLifecycleSchema.nullable(),
    /** True when the review takes a rationale (draft, or returned). */
    editable: z.boolean(),
    rulebookVersion: z.string(),
    /** Each flag of the review, the live ones and the history. */
    flags: z.array(suitabilityFlagDtoSchema),
    reliance: z.object({
        level: z.enum(['not_reliant', 'amber', 'red']),
        indicators: z.array(z.string()),
        unknown: z.array(z.string()),
    }),
    /**
     * What the browser needs for a live preview of the flags (SEP-342 item 11).
     * It holds the coded facts of the saved review. The server stays the
     * record: a save runs the real rulebook. Null when the reader may not
     * edit the review, or may not read the fact find.
     */
    preview: z
        .object({
            /** The facts as canonical JSON, the same shape as the version snapshot. */
            facts: z.custom<InternalReviewFactsSnapshot>(
                (value) => typeof value === 'object' && value !== null,
            ),
            previousStrategyRecommendation: z.string().nullable(),
        })
        .nullable()
        .optional(),
    /** The reasons that hold the sign-off now. */
    blockers: z.array(suitabilityFlagBlockerSchema),
    counts: z.object({ red: z.number(), amber: z.number(), info: z.number() }),
});
export type SuitabilityFlagsResponse = z.infer<typeof suitabilityFlagsResponseSchema>;

/** One row of the firm-wide register of Compliance. */
export const complianceFlagRowSchema = suitabilityFlagDtoSchema.extend({
    clientName: z.string(),
    investmentManagerName: z.string().nullable(),
});
export type ComplianceFlagRow = z.infer<typeof complianceFlagRowSchema>;

const rationaleRequestSchema = z.object({ rationale: z.string().trim().min(1).max(8000) });
const noteRequestSchema = z.object({ note: z.string().trim().min(1).max(8000) });

export const suitabilityFlagsContract = c.router(
    {
        flagsForClient: {
            method: 'GET',
            path: '/desk/suitability/:clientId/flags',
            responses: { 200: suitabilityFlagsResponseSchema, 404: errorBody },
            summary: 'The flags of the newest Internal Review of a client (SEP-293 section 8)',
        },
        recordRationale: {
            method: 'POST',
            path: '/desk/suitability/flags/:flagId/rationale',
            body: rationaleRequestSchema,
            responses: {
                200: suitabilityFlagsResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'The IM records the challenge and the rationale against one flag',
        },
        recordComplianceNote: {
            method: 'POST',
            path: '/comply/suitability/flags/:flagId/note',
            body: noteRequestSchema,
            responses: {
                200: suitabilityFlagsResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Compliance records a note against one flag',
        },
        complianceRegister: {
            method: 'GET',
            path: '/comply/suitability/flags',
            responses: { 200: z.object({ rows: z.array(complianceFlagRowSchema) }) },
            summary: 'The live flags that Compliance sees, across the firm',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
